import { I18n } from "@assets/strings";
import React, { useEffect, useMemo, useRef } from "react";
import { useHistory } from "@core/routing/useHistory";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { CompanyTab, companyTabs, InstituteTab, instituteTabs, Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile, useOrganizationAlias } from "@domain/currentProfile/hooks";
import { useOrganization } from "@domain/organization/hooks";
import { useIsMobile, useScrollToHash, useUnmount } from "@utils/hooks";
import { AppAuthIncentive } from "@ui/components/appAuthIncentive";
import { useAuthModalBlocker } from "@ui/components/auth/useAuthModalBlocker";
import { stack } from "@ui/components/layout";
import { ScrollBehavior } from "@ui/components/navigation/scrollToTop";
import { Page } from "@ui/components/page";
import { CreatePostInput } from "@ui/components/post/createPostInput";
import { FeedList } from "@ui/components/post/feedList";
import { TabSelector } from "@ui/components/tabSelector";
import { gap, mobile } from "@ui/styles/utils";
import { NotFoundPage } from "@ui/pages/notFoundPage";
import { ContentList } from "@ui/pages/profilePage/content/contentList";
import { OrganizationAccreditations } from "@ui/pages/organizationPage/accreditation/organizationAccreditations";
import { JoinOrganization } from "@ui/pages/organizationPage/community/joinOrganization";
import { OrganizationMembers } from "@ui/pages/organizationPage/community/organizationMembers";
import { TeachersList } from "@ui/pages/organizationPage/community/teachersList";
import { OrganizationAssets } from "./organizationAssets";
import { OrganizationDetails } from "./organizationDetails";
import { OrganizationHeader } from "./organizationHeader";
import { OrganizationPresentation } from "./organizationPresentation";
import { OrganizationSwitch } from "./organizationSwitch";
import { OrganizationTracks, OrganizationTracksRef } from "@ui/pages/organizationPage/tracks/organizationTracks";
// import { LivecoursesCarouselOrganization } from "../livecourses/livecoursesCarousel";
import { OrganizationShareroom } from "./organisationShareroom";

interface OrganizationPageProps extends React.HTMLAttributes<HTMLDivElement> {
	organizationId?: string;
	organizationSlug?: string;
	tab?: InstituteTab | CompanyTab;
}
export const OrganizationPage: React.FC<OrganizationPageProps> = ({
	organizationId,
	organizationSlug,
	tab = "activity",
	...htmlProps
}) => {
	const { result: organization, loading, error } = useOrganization(organizationId ?? "", organizationSlug);
	const { currentProfileService } = useServices();
	const organizationAlias = useOrganizationAlias();
	const isMobile = useIsMobile();
	const isAdmin =
		organization?.userRoles.includes("ADMINISTRATOR") ||
		organization?.userRoles.includes("SUPER_ADMINISTRATOR") ||
		false;
	const isTeacher = organization?.userRoles.includes("CONTRIBUTOR");
	const tracksRef = useRef<OrganizationTracksRef>(null);
	const history = useHistory();
	const currentProfile = useCurrentProfile();
	// const contentCountList = organization?.contentCountList
	const displayedTabs: readonly (InstituteTab | CompanyTab)[] = useMemo(() => {
		const usedTabs = organization?.type === "INSTITUTE" ? instituteTabs : companyTabs;
		if (organizationAlias) {
			return usedTabs;
		}

		if (organization) {
			// const hideContent = !organization?.courseCount && !organization?.quizCount;
			const hideCommunity = !organization.memberCounts.CONTRIBUTOR;
			const hideTracks = !organization.trackCount;
			return usedTabs.filter(
				(tab) =>
					// (tab !== "content" || !hideContent) &&
					(tab !== "community" || !hideCommunity) && (tab !== "tracks" || !hideTracks)
			);
		}
		return companyTabs;
	}, [
		organization?.type,
		organizationAlias,
		organization?.courseCount,
		organization?.memberCounts.ADMINISTRATOR,
		organization?.memberCounts.CONTRIBUTOR,
	]);

	// console.log("displayedTabs", displayedTabs);

	useEffect(() => {
		currentProfileService
			.getAlias()
			.toPromise()
			.then((res) => {
				const status = res ? res : isAdmin ? organization : null;
				currentProfileService.setAlias(status);
			});
	}, [organization]);

	useUnmount(() => currentProfileService.setAlias(null), []);

	useScrollToHash(100);

	const blockGuest = useAuthModalBlocker();

	if (error?.code === 404) {
		return <NotFoundPage message={I18n.t("404.organization")} />;
	}

	// TODO : Investigate why Mouaad has done this below since commit #36d79e3
	// return !currentProfile || !organization || loading ? (
	// 	<ClipLoader />

	// Revert Change on stable commit f095ad0
	return !organization ? (
		loading ? (
			<ClipLoader />
		) : null
	) : (
		<Container {...htmlProps}>
			{/* <HelmetInfos
				title={I18n.t("seo.organization.title", { name: organization.primaryName })}
				description={[
					organization.countryCode && countries[organization.countryCode],
					organization.subType && I18n.t(`organization.type.${organization.subType}`),
					`${readableCount(organization.followerCount)} ${I18n.t("profile.card.followers")}`,
					organization.description,
					organization.presentation?.getPlainText(),
					organization.categories.map((cat) => I18n.t(`category.${cat}`)).join(", "),
				]
					.filter(Boolean)
					.join(" - ")}
			/> */}
			{/* <Seo
				type="website"
				title={I18n.t("seo.organization.title", { name: organization.primaryName })}
				description={organization.description}
				ogImage={organization.banner?.url}
			/> */}
			{isAdmin && <OrganizationSwitch organization={organization} />}
			<OrganizationHeader organization={organization} />
			<StyledTabSelector
				id="org-tab"
				items={displayedTabs}
				scrollToEnd={tab === "community"}
				selectedIndex={
					organization?.type === "INSTITUTE" ? displayedTabs.indexOf(tab) : displayedTabs.indexOf(tab as CompanyTab)
				}
				// selectedIndex={
				// 	organization?.type === "INSTITUTE"
				// 		? (displayedTabs.filter((tab) => tab !== "events") as InstituteTab[]).indexOf(tab)
				// 		: (displayedTabs.filter((tab) => tab !== "events") as CompanyTab[]).indexOf(tab as CompanyTab)
				// }
				onItemSelected={(index) => {
					tracksRef.current?.reset();
					const navigate = (): void =>
						history.push(
							Path.Organization(organization, index === 0 ? undefined : displayedTabs[index]),
							ScrollBehavior.NoScroll
						);
					if (displayedTabs[index] === "community" || displayedTabs[index] === "content") {
						blockGuest(navigate);
					} else {
						navigate();
					}
				}}
				itemRenderer={(item) =>
					item === "activity"
						? I18n.t("organization.tab.activity")
						: item === "presentation"
							? I18n.t("organization.tab.presentation")
							: item === "content"
								? I18n.t("organization.tab.content")
								: item === "community"
									? I18n.t("organization.tab.community")
									: // : item === "events"
										// ? I18n.t("live.title")
										item === "shareroom"
										? I18n.t("organization.tab.shareroom")
										: I18n.t("organization.tab.tracks")
				}
			/>
			{
				// tab === "events" ? (
				// 	<Events>
				// 		<LivecoursesCarouselOrganization isAdmin={organizationAlias !== null} organizationId={organization.id} />
				// 	</Events>
				// ) :
				tab === "activity" ? (
					<OrganizationActivity hideTeachers={organization.type === "INSTITUTE"}>
						<FeedContainer>
							{organizationAlias && <CreatePostInput />}
							<FeedList user={organization} />
						</FeedContainer>
						{organization.type === "INSTITUTE" ? (
							!isMobile &&
							!!currentProfile && (
								<StyledTeachersList
									organizationId={organization.id}
									onShouldSeeMore={() =>
										history.push(Path.Organization(organization, "community"), ScrollBehavior.NoScroll)
									}
								/>
							)
						) : (
							<OrganizationDetailsCell organization={organization} />
						)}
					</OrganizationActivity>
				) : tab === "presentation" ? (
					<>
						<OrganizationPresentation organization={organization} />
						<OrganizationAssets organization={organization} />
						<OrganizationAccreditations organization={organization} />
					</>
				) : tab === "content" ? (
					<OrganizationCourses>
						<CoursesCell>
							{/* {isTeacher && (
								<Link to={Path.Lms.Editor("new")}>
									<PrimaryButton>
										{I18n.t("organization.course.incentive", { institute: organization.primaryName })}
									</PrimaryButton>
								</Link>
							)} */}
							<ContentList organizationName={organization.primaryName} isTeacher={isTeacher} user={organization} />
						</CoursesCell>
						{!isMobile && (
							<StyledTeachersList
								organizationId={organization.id}
								onShouldSeeMore={() =>
									history.push(Path.Organization(organization, "community"), ScrollBehavior.NoScroll)
								}
							/>
						)}
					</OrganizationCourses>
				) : tab === "community" ? (
					<>
						<OrganizationCommunity>
							<OrganizationMembersCell organization={organization} />
							<JoinOrganizationCell organization={organization} />
						</OrganizationCommunity>
					</>
				) : tab === "shareroom" ? (
					<OrganizationShareroom organisationId={organization.id as string} />
				) : (
					<OrganizationTracks organizationId={organization.id} ref={tracksRef} />
				)
			}
			{!currentProfile && (
				<AppAuthIncentive message={I18n.t("organization.auth.incentive", { name: organization.primaryName })} />
			)}
		</Container>
	);
};

const Container = styled(Page)`
	${gap(45)};
	@media ${mobile} {
		${gap(30)};
	}
`;

const StyledTabSelector = styled(TabSelector)`
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
	padding: 15px 40px;
	/* margin-bottom: 50px !important; */
`;

// const Events = styled.div``;

const OrganizationActivity = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "hideTeachers",
})<{ hideTeachers: boolean }>`
	display: grid;
	column-gap: 45px;
	row-gap: 45px;
	grid-template-columns: minmax(0, 1fr) 280px;
	grid-template-rows: auto;
	grid-template-areas:
		"feed teachers-or-details"
		"feed ."
		"feed .";

	@media ${mobile} {
		grid-template-columns: 100%;

		grid-template-areas:
			"teachers-or-details"
			"feed";

		${({ hideTeachers }) => hideTeachers && 'grid-template-areas: "feed"'};
	}
`;

const FeedContainer = styled.div`
	grid-area: feed;
	${gap(20)};
`;

const StyledTeachersList = styled(TeachersList)`
	grid-area: teachers-or-details;
`;
const OrganizationDetailsCell = styled(OrganizationDetails)`
	grid-area: teachers-or-details;
`;

const OrganizationCommunity = styled.div`
	display: grid;
	column-gap: 45px;
	row-gap: 45px;
	grid-template-columns: auto 348px;
	grid-template-rows: auto;
	grid-template-areas:
		"teachers join"
		"teachers ."
		"teachers .";

	@media ${mobile} {
		grid-template-columns: 100%;

		grid-template-areas:
			"join"
			"teachers";
	}
`;
const OrganizationMembersCell = styled(OrganizationMembers)`
	grid-area: teachers;
`;
const JoinOrganizationCell = styled(JoinOrganization)`
	grid-area: join;
`;

const OrganizationCourses = styled.div`
	display: grid;
	column-gap: 45px;
	row-gap: 45px;
	grid-template-columns: auto 280px;
	grid-template-rows: auto;
	grid-template-areas:
		"courses teachers-or-details"
		"courses ."
		"courses .";

	@media ${mobile} {
		grid-template-columns: 100%;

		grid-template-areas: "courses";
	}
`;

const CoursesCell = styled.div`
	grid-area: courses;
	${stack(20, "flex-start", "stretch")};

	> a {
		align-self: flex-start;
	}
`;
